import api from '../../../../api/client';

// ---------------------------------------------------------------------------
// STATIC OPTIONS
// ---------------------------------------------------------------------------

export const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => ({
    value: currentYear - 2 + i,
    label: String(currentYear - 2 + i),
  }));
})();

export const GENDER_OPTIONS = [
  { value: 'All', label: 'All Genders' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

export const STATUS_OPTIONS = [
  { value: 'All', label: 'All Status' },
  { value: 'Processed', label: 'Processed' },
  { value: 'Pending', label: 'Pending' },
];

export const TOOLS_OPTIONS = [
  { key: 'print', label: 'Print Report', icon: 'print' },
  { key: 'export_excel', label: 'Export Excel', icon: 'description' },
  { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
  { key: 'rollback', label: 'Rollback Process', icon: 'history', danger: true },
];

// ---------------------------------------------------------------------------
// PREDEFINED REPORTS CONFIG (mirrors DAL.GetReports, GetAttendanceHist)
// ---------------------------------------------------------------------------

export const MONTHLY_ATTENDANCE_PREDEFINED_REPORTS = [
  {
    reportKey: 'monthly_attendance_summary',
    title: 'Monthly Attendance Summary Report',
    description:
      'Complete monthly attendance summary per employee — present days, absent days, with company, branch, and department breakdown.',
    defaultColumns: [
      'Company', 'Branch', 'Department', 'Emp ID', 'Employee Name',
      'Month', 'Year', 'Present Days', 'Absent Days',
    ],
  },
  {
    reportKey: 'monthly_payable_days',
    title: 'Monthly Payable Days Report',
    description:
      'Payable days report — total working days in month vs. actual payable present days used for salary computation.',
    defaultColumns: [
      'Company', 'Branch', 'Department', 'Emp ID', 'Employee Name',
      'Month', 'Year', 'Total Days', 'Payable Days', 'Absent Days',
    ],
  },
  {
    reportKey: 'monthly_emp_attendance_history',
    title: 'Employee Attendance History Report',
    description:
      'Day-wise punch history for the month — biometric swipes, manual punches, attendance status, and remarks.',
    defaultColumns: [
      'Emp ID', 'Employee Name', 'Description', 'Date', 'Day',
      'First Swipe', 'Last Swipe', 'Manual Punch', 'Attendance', 'Remarks',
    ],
  },
];

// ---------------------------------------------------------------------------
// MONTH NAME HELPER
// ---------------------------------------------------------------------------

export function getMonthName(monthNum) {
  const opt = MONTH_OPTIONS.find((m) => m.value === Number(monthNum));
  return opt ? opt.label : '';
}

// ---------------------------------------------------------------------------
// MAP RESPONSE ITEM → UI ROW
// ---------------------------------------------------------------------------

function mapMonthlyItem(item) {
  const month = item.Month ?? item.month ?? 0;
  return {
    id: item.ID ?? item.id ?? 0,
    empId: item.EmpId ?? item.emp_id ?? 0,
    empCode: item.emp_code || '-',
    empName: item.emp_name || '-',
    companyName: item.company_name || '-',
    branchName: item.branch_name || '-',
    departmentName: item.department_name || '-',
    empGroupName: item.emp_group_name || '-',
    month: month,
    monthName: item.month_name || getMonthName(month),
    year: item.Year ?? item.year ?? 0,
    totalDays: item.total_days ?? 0,
    presentDays: parseFloat(item.PresentDays ?? item.present_days ?? 0),
    absentDays: parseFloat(item.AbsentDays ?? item.absent_days ?? 0),
    processId: item.ProcessId ?? item.process_id ?? 0,
    status: item.status || 'Pending',
  };
}

// ---------------------------------------------------------------------------
// API CLIENT
// ---------------------------------------------------------------------------

export const monthlyProcessApi = {

  /** Dynamic dropdown options (companies, branches, departments, groups, genders) */
  getFilterOptions: async () => {
    try {
      const { data } = await api.get('/payroll/monthly-process/options');
      return data;
    } catch {
      return { companies: [], branches: [], departments: [], groups: [], genders: GENDER_OPTIONS };
    }
  },

  /** Paginated monthly attendance list */
  getRecords: async (filters = {}) => {
    const params = {
      month: filters.month ?? new Date().getMonth() + 1,
      year: filters.year ?? new Date().getFullYear(),
      page: filters.page ?? 1,
      page_size: filters.pageSize ?? 20,
    };
    if (filters.companyId && filters.companyId > 0) params.company_id = filters.companyId;
    if (filters.branchId && filters.branchId > 0) params.branch_id = filters.branchId;
    if (filters.deptId && filters.deptId > 0) params.dept_id = filters.deptId;
    if (filters.empId && filters.empId > 0) params.emp_id = filters.empId;
    if (filters.empGroupId && filters.empGroupId > 0) params.emp_group_id = filters.empGroupId;
    if (filters.gender && filters.gender !== 'All') params.gender = filters.gender;
    if (filters.status && filters.status !== 'All') params.status = filters.status;
    if (filters.search && filters.search.trim()) params.search = filters.search.trim();

    const { data } = await api.get('/payroll/monthly-process/', { params });
    return {
      items: (data?.items || []).map(mapMonthlyItem),
      total_count: data?.total_count ?? 0,
      total_pages: data?.total_pages ?? 1,
      page: data?.page ?? 1,
      page_size: data?.page_size ?? 20,
      processed_count: data?.processed_count ?? 0,
    };
  },

  /** Execute monthly attendance processing */
  runProcess: async ({ month, year, companyId, branchId, deptId, empId } = {}) => {
    const payload = { month, year };
    if (companyId && companyId > 0) payload.company_id = companyId;
    if (branchId && branchId > 0) payload.branch_id = branchId;
    if (deptId && deptId > 0) payload.dept_id = deptId;
    if (empId && empId > 0) payload.emp_id = empId;
    const { data } = await api.post('/payroll/monthly-process/run', payload);
    return data;
  },

  /** Rollback monthly attendance — returns 400 if salary is processed */
  rollbackProcess: async ({ month, year, companyId, branchId, deptId } = {}) => {
    const payload = { month, year };
    if (companyId && companyId > 0) payload.company_id = companyId;
    if (branchId && branchId > 0) payload.branch_id = branchId;
    if (deptId && deptId > 0) payload.dept_id = deptId;
    const { data } = await api.post('/payroll/monthly-process/rollback', payload);
    return data;
  },

  /** Fetch report dataset for PredefinedReportsDrawer */
  fetchReportData: async (reportKey, filters = {}) => {
    const params = {
      month: filters.month ?? new Date().getMonth() + 1,
      year: filters.year ?? new Date().getFullYear(),
    };
    if (filters.companyId && filters.companyId > 0) params.company_id = filters.companyId;
    if (filters.branchId && filters.branchId > 0) params.branch_id = filters.branchId;
    if (filters.deptId && filters.deptId > 0) params.dept_id = filters.deptId;
    if (filters.empId && filters.empId > 0) params.emp_id = filters.empId;
    const { data } = await api.get(`/payroll/monthly-process/reports/${reportKey}`, { params });
    return data;
  },

  /** Update present/absent days manually */
  updateRecord: async (id, payload) => {
    const { data } = await api.put(`/payroll/monthly-process/${id}`, {
      present_days: payload.presentDays,
      absent_days: payload.absentDays,
    });
    return data;
  },

  /** Delete single monthly record */
  deleteRecord: async (id) => {
    const { data } = await api.delete(`/payroll/monthly-process/${id}`);
    return data;
  },
};
